import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  EventTicketSetup,
  deleteEventPlayguideSource,
  getEventPlayguideAvailability,
  getEventTicketSetup,
  saveEventTicketSetup,
} from '../../../api/playguides';
import {
  isoToDatetimeLocalJST,
  jstDatetimeLocalToUtcIso,
} from '../../../utils/datetime';
import DsButton from '../../common/DsButton';
import DsModal from '../../common/DsModal';
import { useToast } from '../../common/ToastSystem';
import { useConfirm } from '../../../design-system/ConfirmDialog';
import EventTicketSalesPeriodModal from './EventTicketSalesPeriodModal';
import EventTicketTypesModal from './EventTicketTypesModal';
import PlayguideTicketSourcePanel from './PlayguideTicketSourcePanel';
import {
  AutomaticTicketGuide,
  AutomaticTicketSitePickerOptions,
} from './eventForm/AutomaticTicketSitePickerModal';
import '../../../css/admin/events/EventTicketSetupModal.css';

type Props = {
  open: boolean;
  eventUuid: string;
  onClose: () => void;
  onSaved?: () => void | Promise<void>;
  initialAutomaticGuide?: AutomaticTicketGuide | null;
};

type ToastApi = { success: (message: string) => void; error: (message: string) => void };

const guideLabel = (guideCode: EventTicketSetup['sites'][number]['guideCode'] | AutomaticTicketGuide) =>
  guideCode === 'teket'
    ? 'Teket'
    : guideCode === 'pia'
      ? 'チケットぴあ'
      : guideCode === 'lawson'
        ? 'ローチケ'
        : 'イープラス';

const isAutomaticTicketGuide = (guideCode: unknown): guideCode is AutomaticTicketGuide =>
  guideCode === 'teket' || guideCode === 'eplus' || guideCode === 'pia' || guideCode === 'lawson';

const normalizeSetup = (loaded: EventTicketSetup): EventTicketSetup => ({
  ...loaded,
  performances: loaded.performances || [],
  ticketSalesStartAt: isoToDatetimeLocalJST(loaded.ticketSalesStartAt) || '',
  ticketSalesEndAt: isoToDatetimeLocalJST(loaded.ticketSalesEndAt) || '',
  sites: loaded.sites.map((site) => ({
    ...site,
    salesStartAt: isoToDatetimeLocalJST(site.salesStartAt) || null,
    salesEndAt: isoToDatetimeLocalJST(site.salesEndAt) || null,
  })),
});

export default function EventTicketSetupModal({
  open,
  eventUuid,
  onClose,
  onSaved,
  initialAutomaticGuide = null,
}: Props) {
  const toast = useToast() as unknown as ToastApi;
  const confirmDialog = useConfirm();
  const [setup, setSetup] = useState<EventTicketSetup | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [selectedAutomaticGuide, setSelectedAutomaticGuide] = useState<AutomaticTicketGuide | null>(null);
  const [automaticPanelMode, setAutomaticPanelMode] = useState<'add' | 'edit'>('add');
  const [availableAutomaticGuides, setAvailableAutomaticGuides] = useState<AutomaticTicketGuide[]>([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  const [ticketTypesOpen, setTicketTypesOpen] = useState(false);
  const [salesPeriodOpen, setSalesPeriodOpen] = useState(false);
  const automaticSectionRef = useRef<HTMLElement | null>(null);

  const load = useCallback(async () => {
    if (!eventUuid) return;
    setLoading(true);
    setAvailabilityLoading(true);
    setLoadError('');
    setAvailabilityError('');
    try {
      const [loaded, availabilityResult] = await Promise.all([
        getEventTicketSetup(eventUuid),
        getEventPlayguideAvailability(eventUuid)
          .then((availability) => ({ availability, error: '' }))
          .catch((error: any) => ({
            availability: null,
            error: error?.message || '利用可能な自動取得サイトを確認できませんでした。',
          })),
      ]);
      const normalized = normalizeSetup(loaded);
      const availableGuides = (availabilityResult.availability?.availableGuideCodes || [])
        .filter((guide): guide is AutomaticTicketGuide => isAutomaticTicketGuide(guide));
      setSetup(normalized);
      setAvailableAutomaticGuides(availableGuides);
      setAutomaticPanelMode('add');
      setSelectedAutomaticGuide(
        initialAutomaticGuide && availableGuides.includes(initialAutomaticGuide)
          ? initialAutomaticGuide
          : null
      );
      setAvailabilityError(availabilityResult.error);
      if (
        initialAutomaticGuide &&
        availableGuides.includes(initialAutomaticGuide) &&
        (!normalized.ticketSalesStartAt || !normalized.ticketSalesEndAt)
      ) {
        setSalesPeriodOpen(true);
      }
    } catch (error: any) {
      setLoadError(error?.message || '自動入力設定を取得できませんでした。');
    } finally {
      setLoading(false);
      setAvailabilityLoading(false);
    }
  }, [eventUuid, initialAutomaticGuide]);

  useEffect(() => {
    if (open) void load();
    else {
      setSetup(null);
      setSelectedAutomaticGuide(null);
      setAutomaticPanelMode('add');
      setAvailableAutomaticGuides([]);
      setAvailabilityError('');
      setTicketTypesOpen(false);
      setSalesPeriodOpen(false);
    }
  }, [load, open]);

  useEffect(() => {
    if (!selectedAutomaticGuide) return;
    const timer = window.setTimeout(() => {
      automaticSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [selectedAutomaticGuide, automaticPanelMode]);

  const ticketOptions = useMemo(() => (setup?.tickets || []).map((ticket, index) => ({
    ticketId: ticket.id,
    ticketIndex: index,
    ticketType: ticket.ticketType,
    price: Number(ticket.price || 0),
    quantity: Number(ticket.quantity || 0),
    eventPerformanceId: ticket.eventPerformanceId ?? null,
    performanceName: setup?.performances.find((performance) => performance.id === ticket.eventPerformanceId)?.name || null,
    performanceDate: setup?.performances.find((performance) => performance.id === ticket.eventPerformanceId)?.date || null,
    performanceStartTime: setup?.performances.find((performance) => performance.id === ticket.eventPerformanceId)?.startTime || null,
  })), [setup?.performances, setup?.tickets]);

  const automaticSites = useMemo(() => (setup?.sites || []).filter((site) => (
    site.salesEntryMode === 'automatic' && isAutomaticTicketGuide(site.guideCode)
  )), [setup?.sites]);

  const persistSetup = async ({
    tickets = setup?.tickets || [],
    startAt = setup?.ticketSalesStartAt || '',
    endAt = setup?.ticketSalesEndAt || '',
    successMessage,
  }: {
    tickets?: EventTicketSetup['tickets'];
    startAt?: string | null;
    endAt?: string | null;
    successMessage: string;
  }): Promise<boolean> => {
    if (!setup) return false;
    const startIso = startAt ? jstDatetimeLocalToUtcIso(startAt) : null;
    const endIso = endAt ? jstDatetimeLocalToUtcIso(endAt) : null;
    if ((startIso && !endIso) || (!startIso && endIso) || (startIso && endIso && startIso > endIso)) {
      toast.error('イベント共通の券売開始日時と終了日時を正しく入力してください。');
      return false;
    }
    const invalidTicket = tickets.some((ticket) => (
      !ticket.ticketType.trim() ||
      Number(ticket.price) < 0 ||
      Number(ticket.quantity) < Number(ticket.sold || 0) ||
      (setup.performances.length > 1 && ticket.eventPerformanceId === undefined)
    ));
    if (invalidTicket) {
      toast.error('チケット名・対象・金額・上限枚数を確認してください。上限は販売済み枚数以上が必要です。');
      return false;
    }
    setSaving(true);
    try {
      const saved = await saveEventTicketSetup(eventUuid, {
        ticketSalesStartAt: startIso,
        ticketSalesEndAt: endIso,
        tickets: tickets.map((ticket) => ({
          ...ticket,
          price: Number(ticket.price || 0),
          quantity: Number(ticket.quantity || 0),
        })),
        sites: setup.sites.map((site) => ({
          id: site.id,
          siteName: site.siteName,
          siteUrl: site.siteUrl,
          siteFee: Number(site.siteFee || 0),
          salesEntryMode: site.salesEntryMode,
          salesScheduleMode: site.salesScheduleMode,
          salesStartAt: site.salesScheduleMode === 'custom' && site.salesStartAt
            ? jstDatetimeLocalToUtcIso(site.salesStartAt)
            : null,
          salesEndAt: site.salesScheduleMode === 'custom' && site.salesEndAt
            ? jstDatetimeLocalToUtcIso(site.salesEndAt)
            : null,
        })),
      });
      setSetup(normalizeSetup(saved));
      toast.success(successMessage);
      await onSaved?.();
      return true;
    } catch (error: any) {
      toast.error(error?.message || 'イベントのチケット設定を保存できませんでした。');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const selectAutomaticGuide = (guide: AutomaticTicketGuide) => {
    setAutomaticPanelMode('add');
    setSelectedAutomaticGuide(guide);
    if (!setup?.ticketSalesStartAt || !setup?.ticketSalesEndAt) setSalesPeriodOpen(true);
  };

  const deleteAutomaticSite = async (site: EventTicketSetup['sites'][number]) => {
    if (!site.sourceId) {
      toast.error('自動取得設定を特定できませんでした。設定を再読み込みしてください。');
      return;
    }
    const confirmed = await confirmDialog({
      message: `${guideLabel(site.guideCode)}との連動を解除しますか？反映済みの券売記録は残り、新しい日の自動取得だけが停止します。`,
      danger: false,
    });
    if (!confirmed) return;
    setSaving(true);
    try {
      const result = await deleteEventPlayguideSource(eventUuid, site.sourceId);
      toast.success(result.historyPreserved
        ? '連動を解除しました。反映済みの券売記録は保持されています'
        : '未使用の自動取得設定を削除しました');
      await load();
      await onSaved?.();
    } catch (error: any) {
      toast.error(error?.message || '自動取得サイトの連動を解除できませんでした。');
    } finally {
      setSaving(false);
    }
  };

  const editAutomaticSiteMappings = (site: EventTicketSetup['sites'][number]) => {
    if (!site.sourceId || !isAutomaticTicketGuide(site.guideCode)) {
      toast.error('自動取得設定を特定できませんでした。設定を再読み込みしてください。');
      return;
    }
    setAutomaticPanelMode('edit');
    setSelectedAutomaticGuide(site.guideCode);
  };

  return (
    <DsModal
      open={open}
      onClose={onClose}
      title="チケットサイトと連動"
      ariaLabel="チケットサイトと連動"
      size="lg"
      className="event-ticket-setup-modal"
      bodyClassName="event-ticket-setup-modal__body"
      dismissible={!saving}
      footer={null}
    >
      {loading ? <p className="event-ticket-setup-modal__state">設定を読み込んでいます…</p> : null}
      {!loading && loadError ? (
        <div className="event-ticket-setup-modal__state is-error" role="alert">
          <p>{loadError}</p>
          <DsButton variant="secondary" size="sm" onClick={load}>再読み込み</DsButton>
        </div>
      ) : null}
      {!loading && setup ? (
        <div className="event-ticket-setup-flow">
          {!selectedAutomaticGuide ? (
            <>
              <div className="event-ticket-setup-flow__prereqs" role="group" aria-label="イベント側の設定">
                <button
                  type="button"
                  className="event-ticket-setup-chip"
                  aria-label="チケット種別を設定"
                  onClick={() => setTicketTypesOpen(true)}
                >
                  <span className="event-ticket-setup-chip__k">券種</span>
                  <span className="event-ticket-setup-chip__v">{setup.tickets.length}件</span>
                </button>
                <button
                  type="button"
                  className={`event-ticket-setup-chip${setup.ticketSalesStartAt && setup.ticketSalesEndAt ? ' is-set' : ' is-unset'}`}
                  aria-label="券売期間を設定"
                  onClick={() => setSalesPeriodOpen(true)}
                >
                  <span className="event-ticket-setup-chip__k">券売期間</span>
                  <span className="event-ticket-setup-chip__v">{setup.ticketSalesStartAt && setup.ticketSalesEndAt ? '設定済み' : '未設定'}</span>
                </button>
              </div>

              {automaticSites.length ? (
                <section className="event-ticket-setup-flow__linked-sites">
                  <div className="event-ticket-setup-flow__heading">
                    <div><h3>連動中のサイト</h3></div>
                    <p>券種の対応づけや連動の解除ができます。</p>
                  </div>
                  <div className="event-ticket-setup-flow__site-list">
                    {automaticSites.map((site) => (
                      <article key={site.sourceId || site.id} className="event-ticket-setup-flow__linked-site">
                        <div>
                          <strong>{guideLabel(site.guideCode)}</strong>
                          <span>{site.siteName}</span>
                        </div>
                        <div className="event-ticket-setup-flow__site-actions">
                          <DsButton
                            variant="secondary"
                            size="sm"
                            disabled={!site.sourceId || saving}
                            onClick={() => editAutomaticSiteMappings(site)}
                          >
                            券種の対応づけ
                          </DsButton>
                          <DsButton
                            variant="secondary"
                            size="sm"
                            disabled={!site.sourceId || saving}
                            onClick={() => void deleteAutomaticSite(site)}
                          >
                            連動を解除
                          </DsButton>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}

              <section className="event-ticket-setup-flow__picker">
                <div className="event-ticket-setup-flow__heading">
                  <div><h3>サイトを追加</h3></div>
                  <p>選んだサイトの券売状況を、このイベントへ自動で取り込みます。</p>
                </div>
                <AutomaticTicketSitePickerOptions
                  availableGuides={availableAutomaticGuides}
                  loading={availabilityLoading}
                  loadError={availabilityError}
                  showLead={false}
                  onSelect={selectAutomaticGuide}
                />
              </section>
            </>
          ) : (
            <section ref={automaticSectionRef} className="event-ticket-setup-flow__automatic">
              <div className="event-ticket-setup-flow__heading">
                <div>
                  <h3>{automaticPanelMode === 'edit' ? '券種の対応づけ' : `${guideLabel(selectedAutomaticGuide)}と連動`}</h3>
                </div>
                <DsButton
                  variant="secondary"
                  size="sm"
                  onClick={() => setSelectedAutomaticGuide(null)}
                >
                  サイト選択に戻る
                </DsButton>
              </div>
              <div className="event-ticket-setup-flow__support-actions">
                <div>
                  <strong>イベント側の設定</strong>
                  <span>
                    チケット種別 {setup.tickets.length}件 / 券売期間 {setup.ticketSalesStartAt && setup.ticketSalesEndAt ? '設定済み' : '未設定'}
                  </span>
                </div>
                <DsButton variant="secondary" size="sm" onClick={() => setTicketTypesOpen(true)}>
                  チケット種別を設定
                </DsButton>
                <DsButton variant="secondary" size="sm" onClick={() => setSalesPeriodOpen(true)}>
                  券売期間を設定
                </DsButton>
              </div>
              {!setup.ticketSalesStartAt || !setup.ticketSalesEndAt ? (
                <div className="event-ticket-setup-flow__prerequisite" role="status">
                  <p>イベント共通の券売期間が未設定です。「券売予定に合わせる」を利用する場合は先に設定してください。</p>
                  <DsButton variant="secondary" size="sm" onClick={() => setSalesPeriodOpen(true)}>
                    券売期間を設定
                  </DsButton>
                </div>
              ) : null}
              <PlayguideTicketSourcePanel
                variant="settings"
                eventUuid={eventUuid}
                eventName={setup.eventName}
                eventSalesStartAt={jstDatetimeLocalToUtcIso(setup.ticketSalesStartAt) || ''}
                eventSalesEndAt={jstDatetimeLocalToUtcIso(setup.ticketSalesEndAt) || ''}
                ticketOptions={ticketOptions}
                eventPerformances={setup.performances.map((performance, performanceIndex) => ({
                  performanceId: performance.id,
                  performanceIndex,
                  performanceName: performance.name,
                  performanceDate: performance.date,
                  startTime: performance.startTime,
                }))}
                performanceScoped={setup.performances.length > 1}
                canEdit
                embedded
                guideFilter={selectedAutomaticGuide}
                existingSourcesOnly={automaticPanelMode === 'edit'}
                onConfigurationChanged={load}
                onSalesUpdated={onSaved}
              />
            </section>
          )}
        </div>
      ) : null}

      {setup ? (
        <>
          <EventTicketTypesModal
            open={ticketTypesOpen}
            tickets={setup.tickets}
            performances={setup.performances}
            saving={saving}
            onClose={() => setTicketTypesOpen(false)}
            onSave={async (tickets) => {
              const saved = await persistSetup({ tickets, successMessage: 'チケット種別を保存しました' });
              if (saved) setTicketTypesOpen(false);
            }}
          />
          <EventTicketSalesPeriodModal
            open={salesPeriodOpen}
            startAt={setup.ticketSalesStartAt}
            endAt={setup.ticketSalesEndAt}
            saving={saving}
            onClose={() => setSalesPeriodOpen(false)}
            onSave={async ({ startAt, endAt }) => {
              const saved = await persistSetup({ startAt, endAt, successMessage: 'イベント共通の券売期間を保存しました' });
              if (saved) setSalesPeriodOpen(false);
            }}
          />
        </>
      ) : null}
    </DsModal>
  );
}
